import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  searchQuery,
  SEARCH_QUERY_KEY,
} from "../../search/api/searchV2Api";
import type {
  SearchBundleItem,
  SearchContext,
  SearchQueryParams,
  SearchQueryResponse,
} from "../../search/types";
import {
  BrowseTabType,
  Bundle,
  Comic,
  Artist,
  Release,
  ServerListResponse,
} from "../../../types";
import { useMemo } from "react";
import { AxiosResponse } from "axios";

// The API wraps results in the standard envelope: { payload: { results: T } }
type ApiEnvelope<T> = {
  payload: {
    results: T;
    pagination: unknown;
    filters: unknown;
  };
};

// --- Tab ↔ SearchContext mapping ---

const TAB_TO_CONTEXT: Record<BrowseTabType, SearchContext> = {
  [BrowseTabType.comics]: "series",
  [BrowseTabType.releases]: "releases",
  [BrowseTabType.creators]: "creators",
  [BrowseTabType.bundles]: "all", // bundles use DB query via search API, context doesn't matter
};

// --- Lightweight mappers: search items → shapes cards expect ---

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapSeriesToComic(item: any): Comic {
  return {
    entity_type: "Comic",
    id: item.id,
    name: item.name,
    slug: item.slug,
    url: `/series/${item.slug}`,
    share_url: "",
    read_url: `/series/${item.slug}`,
    description: "",
    lang: "",
    year: 0,
    category_name: "",
    status: 0,
    status_name: "ongoing",
    total_releases: item.total_releases,
    total_chapters: 0,
    total_pageviews: 0,
    total_favorites: 0,
    total_published_releases: item.total_published_releases ?? item.total_releases,
    total_published_pages: 0,
    pages_viewed: 0,
    is_active: true,
    can_edit: false,
    edit_url: "",
    image_url: item.cover_image_url ?? "",
    image_small_url: item.cover_image_url ?? "",
    image_tiny_url: item.cover_image_url ?? "",
    image_medium_url: item.cover_image_url ?? "",
    page_thumbnails: [],
    user_read_status: item.read_status ?? null,
    is_favorite: item.is_favorite ?? false,
    is_free: false,
    count_gold: item.count_gold ?? 0,
    artist: {
      id: 0,
      roman_name: item.artist_name ?? "",
      name: item.artist_name ?? "",
    } as Comic["artist"],
    relevance_match: 0,
    update_time: "",
    comic_type_id: 0,
    comic_format_id: 0,
    reading_direction: "ltr",
    comic_update_frequency: 0,
    comic_update_period: 0,
    last_published_release_published_time: "",
    is_augmented: 0,
    type_display: "",
    contributors: [],
    releases: [],
    section_id: 0,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapReleaseToRelease(item: any): Release {
  return {
    entity_type: "Release",
    id: item.id,
    key: item.key ?? "",
    title: item.title ?? "",
    slug: item.slug ?? "",
    thumbnail_url: item.thumbnail_url ?? "",
    image_url: item.thumbnail_url ?? "",
    image_small_url: item.thumbnail_url ?? "",
    localized_name: item.comic_name ?? "",
    localized_comic_name: item.comic_name ?? item.title ?? "",
    comic_slug: item.comic_slug ?? "",
    url: "",
    read_url: "",
    share_url: "",
    lang_display: "",
    lang_id: item.lang_id ?? "",
    artist: {
      id: 0,
      roman_name: item.artist_name ?? "",
      name: item.artist_name ?? "",
    } as Release["artist"],
    comic_id: 0,
    comic: {} as Comic,
    total_pageviews: 0,
    total_comments: 0,
    page_count: 0,
    update_time: "",
    description: "",
    reactions: [],
    page_objects: [],
    has_next_release: false,
    has_previous_release: false,
    is_vertical_format: false,
    is_free: item.is_free ?? false,
    is_nsfw: false,
    comic_facets: [],
    chapter: 0,
    chapter_type: "",
    time_ago: "",
    is_augmented: false,
    augment_blur: 0,
    background_color: "",
    next_release_read_url: "",
    previous_release_read_url: "",
    pdf_download_sale_available: false,
    next_release_order: 0,
    order: 0,
    price: {} as Release["price"],
    ppb_price_point: item.ppb_price_point ?? undefined,
    ppb_sale_price_point: item.ppb_sale_price_point ?? undefined,
    ppb_gold_price_point: item.ppb_gold_price_point ?? undefined,
    ppb_gold_sale_price_point: item.ppb_gold_sale_price_point ?? undefined,
    release_user_purchase: null,
    access_token_download: null,
    tracked_website_url: "",
    paid_access_thumbnail_blur: false,
    can_edit: false,
    edit_url: "",
    premium_only: item.premium_only ?? false,
    is_open: false,
    user_read_status: {} as Release["user_read_status"],
  } as Release;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapCreatorToArtist(item: any): Artist {
  return {
    entity_type: "Artist",
    id: item.id,
    name: item.name ?? "",
    roman_name: item.roman_name ?? item.name ?? "",
    slug: "",
    url: "",
    avatar_url: item.avatar_url ?? "",
    avatar_small_url: item.avatar_small_url ?? "",
    avatar_tiny_url: "",
    artist_type: { name: "Creator" } as Artist["artist_type"],
    country_id: item.country_id ?? 0,
    total_favorites: 0,
    total_projects: 0,
    total_releases: 0,
    last_activity: "",
    user_is_following: item.is_following ?? false,
  } as unknown as Artist;
}

// Enriched bundles arrive as full Bundle objects from the API; minimal items get defaults.
function mapBundleItem(item: SearchBundleItem): Bundle {
  // The enriched API returns the full Bundle shape — pass it through with safe defaults
  const raw = item as Record<string, unknown>;
  return {
    entity_type: "Bundle",
    id: item.id,
    uuid: item.uuid,
    display_name: item.display_name,
    list_id: (raw.list_id as number) ?? 0,
    list_type_id: (raw.list_type_id as number) ?? 0,
    owner_type_id: (raw.owner_type_id as number) ?? 0,
    owner_id: (raw.owner_id as number) ?? 0,
    description: (raw.description as string) ?? "",
    is_public: (raw.is_public as number) ?? 1,
    local_start_time: (raw.local_start_time as string) ?? "",
    local_end_time: (raw.local_end_time as string) ?? "",
    url: (raw.url as string) ?? "",
    api_url: (raw.api_url as string) ?? "",
    user_owns_all_items: (raw.user_owns_all_items as boolean) ?? false,
    expires_on: (raw.expires_on as string | null) ?? null,
    ppb_price_point: (raw.ppb_price_point as number) ?? 0,
    ppb_gold_price_point: (raw.ppb_gold_price_point as number) ?? 0,
    ppb_sale_price_point: (raw.ppb_sale_price_point as number | null) ?? null,
    ppb_gold_sale_price_point: (raw.ppb_gold_sale_price_point as number | null) ?? null,
    price: (raw.price ?? {}) as Bundle["price"],
    gold_price: (raw.gold_price ?? {}) as Bundle["price"],
    contributors: raw.contributors ?? [],
    owner: (raw.owner ?? {}) as Bundle["owner"],
    total_series: (raw.total_series as number) ?? 0,
    total_items: (raw.total_items as number) ?? 0,
    total_pages: (raw.total_pages as number) ?? 0,
    total_pageviews: (raw.total_pageviews as number) ?? 0,
    total_owned: (raw.total_owned as number) ?? 0,
    item_price_points_count: (raw.item_price_points_count ?? []) as Bundle["item_price_points_count"],
    items: (raw.items ?? []) as Bundle["items"],
  };
}

// --- Main hook ---

const PERPAGE = 24;

export type SearchV2Counts = {
  series: number;
  releases: number;
  creators: number;
  bundles: number;
};

type MappedPage = AxiosResponse<ServerListResponse<Comic | Artist | Release | Bundle>>;

/**
 * Convert browse apiParams (URLSearchParams) into a SearchQueryParams object.
 * Passes all params through — the search API will use what it understands.
 */
function apiParamsToSearchFilters(apiParams: URLSearchParams | null): Partial<SearchQueryParams> {
  if (!apiParams) return {};
  const filters: Record<string, string | number | boolean | number[]> = {};

  for (const [key, value] of apiParams.entries()) {
    // lang_id[] needs special handling as array
    if (key === "lang_id[]") continue;
    filters[key] = value;
  }

  const langIds = apiParams.getAll("lang_id[]").map(Number).filter(Boolean);
  if (langIds.length) filters.lang_id = langIds;

  return filters as Partial<SearchQueryParams>;
}

export function useSearchV2API(
  tab: BrowseTabType,
  query: string,
  enabled: boolean,
  apiParams?: URLSearchParams | null,
) {
  // API context is always tab-based; channel filtering is already merged into apiParams by caller
  const searchContext = TAB_TO_CONTEXT[tab];

  // Stable key for the filters so query key changes when filters change
  const filtersKey = apiParams?.toString() ?? "";

  const searchFilters = useMemo(
    () => apiParamsToSearchFilters(apiParams ?? null),
    [filtersKey],
  );

  // --- Paginated results query ---
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    enabled,
    queryKey: [...SEARCH_QUERY_KEY, "browse", searchContext, query, tab, filtersKey],
    queryFn: async (ctx) => {
      const params: SearchQueryParams = {
        ...searchFilters,
        q: query,
        context: searchContext as SearchContext,
        p: ctx.pageParam,
        perpage: PERPAGE,
        enrich: true,
      };
      const response = await searchQuery(params, ctx.signal);
      return response;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const envelope = lastPage.data as unknown as ApiEnvelope<SearchQueryResponse>;
      const results = envelope.payload.results;
      const set =
        tab === BrowseTabType.comics
          ? results.series
          : tab === BrowseTabType.releases
            ? results.releases
            : tab === BrowseTabType.creators
              ? results.creators
              : results.bundles;
      if (!set) return undefined;
      const totalPages = Math.ceil(set.total / PERPAGE);
      return (lastPageParam as number) < totalPages
        ? (lastPageParam as number) + 1
        : undefined;
    },
    refetchOnWindowFocus: false,
  });

  // --- Map search results to entity shapes compatible with cards ---
  const mappedData = useMemo(() => {
    if (!data) return undefined;

    const pages: MappedPage[] = data.pages.map((page) => {
      const envelope = page.data as unknown as ApiEnvelope<SearchQueryResponse>;
      const responseData = envelope.payload.results;
      let results: (Comic | Artist | Release | Bundle)[];

      switch (tab) {
        case BrowseTabType.comics:
          results = (responseData.series?.items ?? []).map(mapSeriesToComic);
          break;
        case BrowseTabType.releases:
          results = (responseData.releases?.items ?? []).map(mapReleaseToRelease);
          break;
        case BrowseTabType.creators:
          results = (responseData.creators?.items ?? []).map(mapCreatorToArtist);
          break;
        case BrowseTabType.bundles:
          results = (responseData.bundles?.items ?? []).map(mapBundleItem);
          break;
        default:
          results = [];
      }

      // Compute actual pagination from the search response totals
      const set =
        tab === BrowseTabType.comics
          ? responseData.series
          : tab === BrowseTabType.releases
            ? responseData.releases
            : tab === BrowseTabType.creators
              ? responseData.creators
              : undefined;
      const totalResults = set?.total ?? results.length;
      const totalPages = Math.ceil(totalResults / PERPAGE);

      // Shape it like a standard AxiosResponse<ServerListResponse<T>>
      return {
        ...page,
        data: {
          payload: {
            results,
            filters: { q: query },
            pagination: {
              page: data.pages.indexOf(page) + 1,
              per_page: PERPAGE,
              total_pages: totalPages,
              total_results: totalResults,
            },
          },
        },
      } as MappedPage;
    });

    return { ...data, pages };
  }, [data, tab, query]);

  // --- Tab counts query (lightweight, perpage=1, always "all" context) ---
  const { data: countsData } = useQuery({
    enabled,
    queryKey: [...SEARCH_QUERY_KEY, "counts", query, filtersKey],
    queryFn: async ({ signal }) => {
      const params: SearchQueryParams = {
        ...searchFilters,
        q: query,
        context: "all",
        perpage: 1,
        enrich: false,
      };
      const response = await searchQuery(params, signal);
      const envelope = response.data as unknown as ApiEnvelope<SearchQueryResponse>;
      const d = envelope.payload.results;
      return {
        series: d.series.total,
        releases: d.releases.total,
        creators: d.creators.total,
        bundles: d.bundles.total,
      } satisfies SearchV2Counts;
    },
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  return {
    data: mappedData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    counts: countsData ?? null,
  };
}
